import { LINK_MESSAGE_TO_CONTACT_MUTATION } from 'app/graphql/messageMutations'
import { gqlFetchList, gqlMutation } from 'app/lib/graphqlClient'
import type { Mutation } from 'app/generated/graphql'
import type { MessageJson } from 'app/types/entities'

const PAGE_LIMIT = 500

export type LinkThreadResult = {
  /** Messages that needed linking (they had no contactId yet). */
  total: number
  linked: number
  failed: number
}

/** Every message in the thread, paged past the per-request cap so long conversations link whole. */
async function fetchAllThreadMessages(threadId: string): Promise<MessageJson[]> {
  const all: MessageJson[] = []
  for (let page = 0; ; page++) {
    const { hits } = await gqlFetchList<MessageJson>(
      `messages`,
      { search: `threadId:"${threadId}"`, pagination: { page, limit: PAGE_LIMIT } },
      `id contactId`,
    )
    all.push(...hits)
    if (hits.length < PAGE_LIMIT) return all
  }
}

/** Ids whose link mutation failed; the mutation never throws, it resolves with an error field. */
async function linkBatch(ids: string[], contactId: string): Promise<string[]> {
  const results = await Promise.all(
    ids.map(async (id) => ({
      id,
      result: await gqlMutation(LINK_MESSAGE_TO_CONTACT_MUTATION, {
        id,
        contactId,
      }),
    })),
  )
  return results.filter((entry) => entry.result.error).map((entry) => entry.id)
}

/**
 * Attribute a conversation to a contact by linking its messages.
 *
 * The API links contact per message (`linkMessageToContact`); there is no atomic
 * thread-level operation yet, so a conversation is linked by sweeping its
 * messages. Two rules keep the sweep safe:
 *
 * - Only messages with no contactId are filled. A message already attributed to
 *   another related contact keeps its attribution.
 * - Failed links are retried once before being reported. Because the sweep only
 *   fills missing links, running it again never double-writes, so a rerun after
 *   a partial failure finishes the job rather than corrupting it.
 */
export async function linkThreadMessagesToContact(threadId: string, contactId: string): Promise<LinkThreadResult> {
  const messages = await fetchAllThreadMessages(threadId)
  const targets = messages.filter((message) => message.id && !message.contactId).map((message) => message.id!)
  let failedIds = await linkBatch(targets, contactId)
  if (failedIds.length > 0) failedIds = await linkBatch(failedIds, contactId)
  return { total: targets.length, linked: targets.length - failedIds.length, failed: failedIds.length }
}
